import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../App'
import { api, Group, GroupMember, GroupInvite } from '../services/api'

export default function GroupInfoPage() {
  const { groupId } = useParams<{ groupId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [group, setGroup] = useState<Group | null>(null)
  const [members, setMembers] = useState<GroupMember[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [invite, setInvite] = useState<GroupInvite | null>(null)
  const [showInvite, setShowInvite] = useState(false)
  const [copied, setCopied] = useState(false)

  const isAdmin = group?.my_role === 'admin' || group?.my_role === 'owner'
  const isOwner = group?.my_role === 'owner'

  useEffect(() => {
    if (groupId) loadGroupInfo()
  }, [groupId])

  const loadGroupInfo = async () => {
    try {
      setIsLoading(true)
      const [groupData, membersData] = await Promise.all([
        api.getGroup(groupId!),
        api.getGroupMembers(groupId!),
      ])
      setGroup(groupData)
      setMembers(membersData)
      setEditName(groupData.name)
      setEditDescription(groupData.description || '')
    } catch (error) {
      console.error('Failed to load group info:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveEdit = async () => {
    if (!groupId || !editName.trim()) return
    try {
      await api.updateGroup(groupId, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      } as any)
      setGroup(prev => prev ? { ...prev, name: editName.trim(), description: editDescription.trim() } : prev)
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to update group:', error)
      alert('更新失敗')
    }
  }

  const handleCreateInvite = async () => {
    if (!groupId) return
    try {
      const inv = await api.createGroupInvite(groupId)
      setInvite(inv)
      setShowInvite(true)
    } catch (error) {
      console.error('Failed to create invite:', error)
      alert('建立邀請連結失敗')
    }
  }

  const handleCopyInvite = async () => {
    if (!invite) return
    try {
      await navigator.clipboard.writeText(invite.url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback
      const input = document.createElement('input')
      input.value = invite.url
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleLeaveGroup = async () => {
    if (!groupId) return
    if (!confirm('確定要離開群組嗎？')) return
    try {
      await api.leaveGroup(groupId)
      navigate('/chats')
    } catch (error) {
      console.error('Failed to leave group:', error)
    }
  }

  const handleDeleteGroup = async () => {
    if (!groupId) return
    if (!confirm('確定要解散群組嗎？此操作不可恢復。')) return
    try {
      await api.deleteGroup(groupId)
      navigate('/chats')
    } catch (error) {
      console.error('Failed to delete group:', error)
      alert('解散群組失敗')
    }
  }

  const handleRemoveMember = async (memberId: string) => {
    if (!groupId) return
    if (!confirm('確定要移除此成員嗎？')) return
    try {
      await api.removeGroupMember(groupId, memberId)
      setMembers(prev => prev.filter(m => m.id !== memberId))
    } catch (error) {
      console.error('Failed to remove member:', error)
    }
  }

  const handleChangeRole = async (memberId: string, currentRole: string) => {
    if (!groupId) return
    const newRole = currentRole === 'admin' ? 'member' : 'admin'
    try {
      await api.updateMemberRole(groupId, memberId, newRole)
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, role: newRole } : m))
    } catch (error) {
      console.error('Failed to change role:', error)
      alert('更改角色失敗')
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg">
        <div className="spinner border-primary border-t-primary-light" />
      </div>
    )
  }

  if (!group) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg">
        <p className="text-gray-500">群組不存在</p>
      </div>
    )
  }

  const roleLabel = (role: string) => {
    switch (role) {
      case 'owner': return '擁有者'
      case 'admin': return '管理員'
      default: return '成員'
    }
  }

  const roleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-yellow-500/20 text-yellow-500'
      case 'admin': return 'bg-blue-500/20 text-blue-500'
      default: return ''
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <header className="app-bar">
        <button onClick={() => navigate(`/group/${groupId}`)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-semibold flex-1">群組資訊</h1>
        {isAdmin && !isEditing && (
          <button onClick={() => setIsEditing(true)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        )}
      </header>

      <main className="flex-1 overflow-y-auto">
        {/* Group Avatar & Info */}
        <div className="flex flex-col items-center py-8 px-4">
          <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center mb-4 shadow-lg">
            {group.avatar_url ? (
              <img src={group.avatar_url} alt={group.name} className="w-full h-full object-cover rounded-full" />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            )}
          </div>

          {isEditing ? (
            <div className="w-full max-w-sm space-y-3">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="input-field text-center text-lg font-semibold"
                placeholder="群組名稱"
              />
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="input-field text-center text-sm resize-none"
                placeholder="群組描述（可選）"
                rows={3}
              />
              <div className="flex gap-2">
                <button onClick={() => setIsEditing(false)} className="flex-1 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
                  取消
                </button>
                <button onClick={handleSaveEdit} className="flex-1 py-2 rounded-xl bg-primary text-white font-medium">
                  儲存
                </button>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{group.name}</h2>
              {group.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-xs">{group.description}</p>
              )}
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">{group.member_count || members.length} 位成員</p>
            </>
          )}
        </div>

        {/* Action Cards */}
        <div className="px-4 space-y-2 mb-4">
          {/* Invite Link */}
          {isAdmin && (
            <button
              onClick={handleCreateInvite}
              className="w-full flex items-center gap-3 px-4 py-3.5 bg-white dark:bg-dark-surface rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <span className="flex-1 text-left font-medium text-gray-900 dark:text-white">產生邀請連結</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Add Members */}
          {isAdmin && (
            <button
              onClick={() => navigate(`/group/${groupId}/members`)}
              className="w-full flex items-center gap-3 px-4 py-3.5 bg-white dark:bg-dark-surface rounded-xl shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
              <span className="flex-1 text-left font-medium text-gray-900 dark:text-white">管理成員</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>

        {/* Members Section */}
        <div className="px-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2 px-1">
            成員 ({members.length})
          </h3>
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
            {members.map(member => (
              <div key={member.id} className="flex items-center gap-3 px-4 py-3">
                <div className="avatar avatar-sm">
                  {member.avatar_url ? (
                    <img src={member.avatar_url} alt={member.username} className="w-full h-full object-cover rounded-full" />
                  ) : (
                    member.username.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{member.username}</p>
                    {member.role !== 'member' && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${roleBadgeColor(member.role)}`}>
                        {roleLabel(member.role)}
                      </span>
                    )}
                  </div>
                </div>
                {/* Actions for owner */}
                {isOwner && member.id !== user?.id && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleChangeRole(member.id, member.role)}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-400 hover:text-primary transition-colors"
                      title={member.role === 'admin' ? '移除管理員' : '設為管理員'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-400 hover:text-red-500 transition-colors"
                      title="移除成員"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="px-4 pb-8 space-y-2">
          <button
            onClick={handleLeaveGroup}
            className="w-full flex items-center gap-3 px-4 py-3.5 bg-white dark:bg-dark-surface rounded-xl shadow-sm hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </div>
            <span className="flex-1 text-left font-medium text-red-500">離開群組</span>
          </button>

          {isOwner && (
            <button
              onClick={handleDeleteGroup}
              className="w-full flex items-center gap-3 px-4 py-3.5 bg-white dark:bg-dark-surface rounded-xl shadow-sm hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <span className="flex-1 text-left font-medium text-red-500">解散群組</span>
            </button>
          )}
        </div>
      </main>

      {/* Invite Modal */}
      {showInvite && invite && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" onClick={() => setShowInvite(false)}>
          <div className="bg-white dark:bg-dark-surface rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">邀請連結</h3>
            <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-3 mb-4">
              <p className="text-sm text-gray-700 dark:text-gray-300 break-all text-center font-mono">{invite.url}</p>
            </div>
            <p className="text-xs text-gray-400 text-center mb-4">
              連結將在 7 天後過期
            </p>
            <button
              onClick={handleCopyInvite}
              className={`w-full py-3 rounded-xl font-medium transition-colors ${
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-primary text-white hover:bg-primary-dark'
              }`}
            >
              {copied ? '✓ 已複製' : '複製連結'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
